import { CornerTable } from "../../../v2/components/CornerTable.jsx";
// src/features/imports/components/DiffRow.jsx
//
// LSE-ADD-DATA-MODAL-S2 / INGEST-REVIEW-SIDEBYSIDE / INGEST-REVIEW-ALIGN
//
// Single import diff row.
//
// INGEST-REVIEW-ALIGN: Scalar variant uses display:contents so its five
// child cells participate directly in the LeaseGroup CSS Grid, giving
// flush vertical column alignment across every diff row in a group.
// Advisory and year_costs variants span all grid tracks via gridColumn "1/-1".
//
// Decision verb vocabulary:
//   Changed fields (currentValue present): "Keep current" / "Use incoming"
//   New fields (currentValue absent):      "Add" / "Skip"
//   testIds: keep-current-btn-{id} / use-incoming-btn-{id}
//            add-btn-{id} / skip-btn-{id}
//
// Props:
//   diff        { id, leaseId, fieldName, diffType, currentValue, incomingValue,
//                 currentProvenance, incomingSource, recommendedAction, status }
//   onDecide    (diffId, decision) => void  — called immediately (optimistic)
//   disabled    boolean — blocks buttons while a request is in-flight

import React from "react";
import { T } from "../../../app/tokens.js";
import { APP_NAME } from "../../../constants/branding.js";
import { ProvenanceTimeline } from "./ProvenanceTimeline.jsx";
import { criticalDateLabel } from "../../../utils/criticalDateLabels.js";

// ─── Shared grid template ─────────────────────────────────────────────────────
// Single source of truth for the five-column diff grid used by LeaseGroup.
// Field · Current · Arrow · Incoming · Decisions

export const DIFF_GRID_COLS =
  "minmax(140px,1fr) minmax(0,2fr) 28px minmax(0,2fr) auto";

// ─── Field label map ──────────────────────────────────────────────────────────

const FIELD_LABELS = {
  startDate:               "Start date",
  endDate:                 "End date",
  breakDate:               "Break date",
  totalCost:               "Total cost",
  currency:                "Currency",
  size:                    "Size (sqft)",
  areaType:                "Area type",
  status:                  "Status",
  type:                    "Type",
  vendor:                  "Vendor / landlord",
  lessee:                  "Lessee",
  breakNoticeMonths:       "Break notice (mo)",
  renewalNoticeMonths:     "Renewal notice (mo)",
  renewalCount:            "Renewal options",
  renewalTermYears:        "Renewal term (yr)",
  escalationType:          "Escalation type",
  escalationRate:          "Escalation rate (%)",
  opexStructure:           "OpEx structure",
  // LEASE-FIELDS-ADD-DROP: camDescription removed (column dropped in 0216)
  opexCapPercent:          "OpEx cap (%)",
  baseYear:                "Base year",
  baseYearOpex:            "Base year OpEx",
  baseYearTax:             "Base year tax",
  securityDepositAmount:   "Security deposit",
  // LEASE-FIELDS-ADD-DROP: securityDepositType + holdoverPercent removed (columns dropped in 0216)
  // 4 new Spacebase fields added (migration 0216)
  possessionDate:          "Possession date",
  lessorContact:           "Lessor contact",
  rentPaymentType:         "Rent payment type",
  termType:                "Term type",
  freeRentMonths:          "Free rent (mo)",
  tiaTotalAmount:          "TIA total",
  holdoverPeriodMonths:    "Holdover period (mo)",
  nextNoticeDeadline:      "Next notice deadline",
  soonestPossibleExpiration: "Soonest expiration",
  permittedUse:            "Permitted use",
  servicedOfficeProvider:  "Serviced office provider",
  sqftMeasurementStandard: "Sqft standard",
  subletConsentRequired:   "Sublet consent reqd",
  subletNotes:             "Sublet notes",
  subletProfitSharePercent: "Sublet profit share (%)",
  subletRecaptureRight:    "Sublet recapture right",
  restorationObligations:  "Restoration obligations",
  rofr_rofo_notes:         "ROFR/ROFO notes",
  expansionOptionNotes:    "Expansion option notes",
  noticePeriodText:        "Notice period text",
  noticeAddress:           "Notice address",
  notes:                   "Notes",
};

function fieldLabel(name) {
  if (FIELD_LABELS[name]) return FIELD_LABELS[name];
  return name
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

// ─── Source badge ─────────────────────────────────────────────────────────────

const SOURCE_BADGE_STYLE = {
  spacebase:       { background: T.spacebaseBlue,         color: T.white, label: "SB" },
  manual:          { background: T.provenanceTooltipManual, color: T.white, label: "M"  },
  pdf_abstraction: { background: T.provenanceTooltipPdf,   color: T.white, label: "P"  },
  computed:        { background: T.forestGreen,            color: T.white, label: "C"  },
};

function SourceBadge({ source }) {
  const s = SOURCE_BADGE_STYLE[source] || { background: T.mid, color: T.white, label: source?.slice(0,2).toUpperCase() || "?" };
  return (
    <span
      title={source}
      style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        width: 20, height: 20, borderRadius: "50%",
        background: s.background, color: s.color,
        fontSize: T.fsXxxs, fontWeight: T.fwDisplay, flexShrink: 0,
      }}
    >
      {s.label}
    </span>
  );
}

// ─── Confidence badge (pdf_abstraction only) ──────────────────────────────────

const CONFIDENCE_STYLE = {
  high:   { background: T.utilGreen,   color: T.utilGreenText, label: "HIGH" },
  medium: { background: T.warnAmberBg, color: T.amberDark,     label: "MED"  },
  low:    { background: T.ragRedBg,    color: T.ragRed,        label: "LOW"  },
};

function ConfidenceBadge({ confidence }) {
  const s = CONFIDENCE_STYLE[confidence] || CONFIDENCE_STYLE.low;
  return (
    <span
      data-testid={`confidence-badge-${confidence}`}
      title={`LLM confidence: ${confidence}`}
      style={{
        fontSize: T.fsXxxs, fontWeight: T.fwBold, padding: "1px 5px",
        borderRadius: 4, background: s.background, color: s.color,
        flexShrink: 0, whiteSpace: "nowrap",
      }}
    >
      {s.label}
    </span>
  );
}

// ─── Recommended-action chip ──────────────────────────────────────────────────

const REC_CHIP = {
  apply:   { bg: T.utilGreen,   color: T.utilGreenText, label: "Apply"   },
  skip:    { bg: T.warnYellowBg, color: T.amberDark,   label: "Skip"    },
  review:  { bg: T.bg,          color: T.mid,           label: "Review"  },
  promote: { bg: T.bluePale,    color: T.blueDeep,      label: "Promote" },
};

function RecChip({ action }) {
  const c = REC_CHIP[action] || REC_CHIP.review;
  return (
    <span
      data-testid="rec-chip"
      style={{
        fontSize: T.fsXxs, fontWeight: T.fwBold, padding: "2px 7px",
        borderRadius: 8, background: c.bg, color: c.color,
        flexShrink: 0, whiteSpace: "nowrap",
      }}
    >
      {c.label}
    </span>
  );
}

// ─── Value formatter ──────────────────────────────────────────────────────────

function fmtValue(v) {
  if (v === null || v === undefined) return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  const s = String(v).trim();
  if (s === "") return "—";
  if (s.includes("T")) {
    try {
      const d = new Date(s);
      if (!isNaN(d.getTime())) {
        return d.toLocaleDateString("en-US", {
          year: "numeric", month: "short", day: "numeric",
        });
      }
    } catch { return s; }
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    try {
      return new Date(s + "T00:00:00").toLocaleDateString("en-US", {
        year: "numeric", month: "short", day: "numeric",
      });
    } catch { return s; }
  }
  // Format numeric values with thousands separators
  if (/^-?\d+(\.\d+)?$/.test(s)) {
    const num = Number(s);
    if (isFinite(num)) {
      const isDecimal = s.includes(".");
      return num.toLocaleString("en-US", {
        minimumFractionDigits: isDecimal ? 2 : 0,
        maximumFractionDigits: isDecimal ? 2 : 0,
      });
    }
  }
  return s;
}

// ─── Material change warning ──────────────────────────────────────────────────
//
// For a targeted set of high-value numeric fields, flag changes that are
// >30% OR represent a ratio >10x (or <0.1x) between current and incoming.
// These magnitudes are unlikely to be typo-free and deserve a reviewer pause.

const MATERIAL_NUMERIC_FIELDS = new Set([
  "size",
  "totalCost",
  "securityDepositAmount",
  "escalationRate",
  "tiaTotalAmount",
]);

function computeMaterialChange(fieldName, currentValue, incomingValue) {
  if (!MATERIAL_NUMERIC_FIELDS.has(fieldName)) return null;
  if (currentValue == null || incomingValue == null) return null;
  const cur = Number(currentValue);
  const inc = Number(incomingValue);
  if (!isFinite(cur) || !isFinite(inc) || cur === 0 || inc === 0) return null;
  const pct   = ((inc - cur) / Math.abs(cur)) * 100;
  const ratio = inc / cur;
  if (Math.abs(pct) > 30 || ratio > 10 || ratio < 0.1) {
    return Math.round(pct);
  }
  return null;
}

function LargeChangeBadge({ pct }) {
  if (pct == null) return null;
  const sign = pct > 0 ? "+" : "";
  return (
    <span
      data-testid="large-change-badge"
      style={{
        fontSize: T.fsXxs, fontWeight: T.fwBold,
        padding: "1px 6px", borderRadius: 6, marginLeft: 4,
        background: T.warnAmberBg, color: T.amberDark,
        flexShrink: 0, whiteSpace: "nowrap",
      }}
    >
      ⚠ Large change ({sign}{Math.round(pct)}%)
    </span>
  );
}

// ─── Numeric delta badge ──────────────────────────────────────────────────────

function computeDelta(current, incoming) {
  const cur = Number(current);
  const inc = Number(incoming);
  if (!isFinite(cur) || !isFinite(inc)) return null;
  if (current == null || incoming == null) return null;
  const delta = inc - cur;
  if (delta === 0) return null;
  return delta;
}

function DeltaBadge({ delta }) {
  if (delta == null) return null;
  const isPos = delta > 0;
  return (
    <span
      data-testid="delta-badge"
      style={{
        fontSize: T.fsXxs, fontWeight: T.fwBold,
        padding: "1px 6px", borderRadius: 6, marginLeft: 6,
        background: isPos ? T.utilGreen : T.utilRed,
        color: isPos ? T.utilGreenText : T.breakRed,
        flexShrink: 0, whiteSpace: "nowrap",
      }}
    >
      {isPos ? `+${delta.toLocaleString("en-US")}` : `\u2212${Math.abs(delta).toLocaleString("en-US")}`}
    </span>
  );
}

// ─── Decision buttons ─────────────────────────────────────────────────────────

function DecideBtn({ label, active, activeStyle, onClick, disabled, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      disabled={disabled}
      onClick={onClick}
      style={{
        padding: "4px 10px", borderRadius: 5, fontSize: T.fsXs, fontWeight: T.fwBold,
        cursor: disabled ? "not-allowed" : "pointer",
        border: `1.5px solid ${active ? activeStyle.border : T.border}`,
        background: active ? activeStyle.bg : T.white,
        color: active ? activeStyle.color : T.mid,
        transition: "background 0.1s, border-color 0.1s",
        opacity: disabled ? 0.5 : 1,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </button>
  );
}

// ─── YearCostTable ────────────────────────────────────────────────────────────

function parseYcArray(v) {
  if (!v) return [];
  if (Array.isArray(v)) return v;
  try { return JSON.parse(v); } catch { return []; }
}

function ycRowsDiffer(cur, inc) {
  if ((cur?.rent == null) !== (inc?.rent == null)) return true;
  if (!cur || !inc) return true;
  return ["rent", "opex", "capex", "totalCost"].some(
    (k) => Number(cur[k] ?? 0) !== Number(inc[k] ?? 0)
  );
}

function YearCostTable({ currentValue, incomingValue, incomingSource }) {
  const currentRows  = parseYcArray(currentValue);
  const incomingRows = parseYcArray(incomingValue);

  const allYears = [...new Set([
    ...currentRows.map((r) => r.year),
    ...incomingRows.map((r) => r.year),
  ])].sort((a, b) => a - b);

  const curMap = new Map(currentRows.map((r) => [r.year, r]));
  const incMap = new Map(incomingRows.map((r) => [r.year, r]));

  const thStyle = {
    fontSize: T.fsXxxs, fontWeight: T.fwBold, color: T.mid, padding: "2px 6px",
    textAlign: "right", whiteSpace: "nowrap", borderBottom: `1px solid ${T.border}`,
  };
  const thYearStyle = { ...thStyle, textAlign: "left" };
  const tdStyle = (changed) => ({
    fontSize: T.fsXxs, padding: "2px 6px", textAlign: "right",
    background: changed ? T.warnYellowBg : "transparent",
    color: changed ? T.amberDark : T.dark,
    whiteSpace: "nowrap",
  });
  const tdYearStyle = (changed) => ({
    ...tdStyle(changed), textAlign: "left", fontWeight: T.fwSemibold,
  });

  const fmtNum = (v) => {
    if (v == null) return "—";
    const num = Number(v);
    if (!isFinite(num)) return String(v);
    const s = String(v);
    const isDecimal = s.includes(".");
    return num.toLocaleString("en-US", {
      minimumFractionDigits: isDecimal ? 2 : 0,
      maximumFractionDigits: isDecimal ? 2 : 0,
    });
  };

  return (
    <div
      data-testid="year-cost-table"
      style={{ display: "flex", gap: 12, flex: 1, minWidth: 0, overflow: "auto" }}
    >
      {/* Current (in WorkplaceOS) */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: T.fsXxxs, fontWeight: T.fwBold, color: T.mid, marginBottom: 2,
          textTransform: "uppercase", letterSpacing: "0.5px",
        }}>
          Current (in {APP_NAME})
        </div>
        <CornerTable style={{ borderCollapse: "collapse", width: "100%" }}>
          <thead>
            <tr>
              <th style={thYearStyle}>Year</th>
              <th style={thStyle}>Rent</th>
              <th style={thStyle}>OpEx</th>
              <th style={thStyle}>Capex</th>
              <th style={thStyle}>Total</th>
              <th style={thStyle}>Curr</th>
            </tr>
          </thead>
          <tbody>
            {allYears.length === 0 ? (
              <tr><td colSpan={6} style={{ fontSize: T.fsXxs, color: T.mid, padding: "4px 6px" }}>—</td></tr>
            ) : allYears.map((yr) => {
              const cur = curMap.get(yr);
              const inc = incMap.get(yr);
              const changed = ycRowsDiffer(cur, inc);
              return (
                <tr key={yr}>
                  <td style={tdYearStyle(changed)}>{yr}</td>
                  <td style={tdStyle(changed)}>{cur ? fmtNum(cur.rent)      : "—"}</td>
                  <td style={tdStyle(changed)}>{cur ? fmtNum(cur.opex)      : "—"}</td>
                  <td style={tdStyle(changed)}>{cur ? fmtNum(cur.capex)     : "—"}</td>
                  <td style={tdStyle(changed)}>{cur ? fmtNum(cur.totalCost) : "—"}</td>
                  <td style={tdStyle(changed)}>{cur?.currency ?? "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </CornerTable>
      </div>

      {/* Arrow */}
      <div style={{
        display: "flex", alignItems: "center", color: T.light,
        fontSize: T.fsMd, flexShrink: 0, paddingTop: 16,
      }}>
        →
      </div>

      {/* Incoming (this import) */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: T.fsXxxs, fontWeight: T.fwBold, color: T.blueDeep, marginBottom: 2,
          textTransform: "uppercase", letterSpacing: "0.5px",
          display: "flex", alignItems: "center", gap: 4,
        }}>
          <SourceBadge source={incomingSource} />
          <span>Incoming (this import)</span>
        </div>
        <CornerTable style={{ borderCollapse: "collapse", width: "100%" }}>
          <thead>
            <tr>
              <th style={thYearStyle}>Year</th>
              <th style={thStyle}>Rent</th>
              <th style={thStyle}>OpEx</th>
              <th style={thStyle}>Capex</th>
              <th style={thStyle}>Total</th>
              <th style={thStyle}>Curr</th>
            </tr>
          </thead>
          <tbody>
            {allYears.length === 0 ? (
              <tr><td colSpan={6} style={{ fontSize: T.fsXxs, color: T.mid, padding: "4px 6px" }}>—</td></tr>
            ) : allYears.map((yr) => {
              const cur = curMap.get(yr);
              const inc = incMap.get(yr);
              const changed = ycRowsDiffer(cur, inc);
              return (
                <tr key={yr}>
                  <td style={tdYearStyle(changed)}>{yr}</td>
                  <td style={tdStyle(changed)}>{inc ? fmtNum(inc.rent)      : "—"}</td>
                  <td style={tdStyle(changed)}>{inc ? fmtNum(inc.opex)      : "—"}</td>
                  <td style={tdStyle(changed)}>{inc ? fmtNum(inc.capex)     : "—"}</td>
                  <td style={tdStyle(changed)}>{inc ? fmtNum(inc.totalCost) : "—"}</td>
                  <td style={tdStyle(changed)}>{inc?.currency ?? "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </CornerTable>
      </div>
    </div>
  );
}

// ─── CriticalDatesTable ───────────────────────────────────────────────────────
//
// Two modes:
//   1. New-import (currentRows empty): shows only the incoming block — same as
//      the original "always []" behaviour; decision buttons are Add / Skip.
//   2. Re-import (currentRows non-empty): shows side-by-side Current vs
//      Incoming columns with row-level addition/removal highlighting — mirrors
//      the YearCostTable layout. Decision buttons switch to Keep current / Use
//      incoming because DecisionArea derives isNewField from diff.currentValue.
//
// Row matching uses type + normalised dueDate as the composite key, so two
// rows of the same type but different dates are treated as distinct entries.

function parseCdArray(v) {
  if (!v) return [];
  if (Array.isArray(v)) return v;
  try { return JSON.parse(v); } catch { return []; }
}

function fmtCdDate(iso) {
  if (!iso) return "—";
  const s = String(iso);
  // Accept YYYY-MM-DD or ISO timestamp
  const date = new Date(s.includes("T") ? s : s + "T00:00:00");
  if (isNaN(date.getTime())) return s;
  return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

// Stable key for matching rows across current and incoming arrays.
function cdKey(cd) {
  const date = String(cd.dueDate ?? cd.due_date ?? "").slice(0, 10);
  return `${cd.type}|${date}`;
}

// ── Shared table styles for the side-by-side variant ──────────────────────────

const CD_TH = {
  fontSize: T.fsXxxs, fontWeight: T.fwBold, color: T.mid, padding: "2px 8px",
  textAlign: "left", whiteSpace: "nowrap", borderBottom: `1px solid ${T.border}`,
};
const cdTd = (highlight) => ({
  fontSize: T.fsXxs, padding: "3px 8px", whiteSpace: "nowrap",
  background: highlight === "added"   ? T.acceptedRowBg
            : highlight === "removed" ? T.redSoft
            : "transparent",
  color: highlight === "added"   ? T.utilGreenText
       : highlight === "removed" ? T.ragRed
       : T.dark,
});
const cdTdDesc = (highlight) => ({
  ...cdTd(highlight), whiteSpace: "normal", maxWidth: 220,
  color: highlight ? cdTd(highlight).color : T.mid,
  fontStyle: "italic",
});

// ── Single-column table (shared markup for both sides in the side-by-side) ────

function CdSingleTable({ rows, highlightMap, testPrefix }) {
  return (
    <CornerTable style={{ borderCollapse: "collapse", width: "100%" }}>
      <thead>
        <tr>
          <th style={CD_TH}>Type</th>
          <th style={CD_TH}>Date</th>
          <th style={CD_TH}>Description</th>
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr>
            <td colSpan={3} style={{ fontSize: T.fsXxs, color: T.light, fontStyle: "italic", padding: "4px 8px" }}>
              No critical dates
            </td>
          </tr>
        ) : rows.map((cd, i) => {
          const hl = highlightMap?.get(cdKey(cd));
          return (
            <tr key={i} data-testid={testPrefix ? `${testPrefix}-${i}` : undefined}>
              <td style={cdTd(hl)}>{criticalDateLabel(cd.type, cd.description)}</td>
              <td style={cdTd(hl)}>{fmtCdDate(cd.dueDate ?? cd.due_date)}</td>
              <td style={cdTdDesc(hl)}>{cd.description || "—"}</td>
            </tr>
          );
        })}
      </tbody>
    </CornerTable>
  );
}

function CriticalDatesTable({ currentValue, incomingValue, incomingSource }) {
  const currentRows  = parseCdArray(currentValue);
  const incomingRows = parseCdArray(incomingValue);

  const thStyle = CD_TH;
  const tdStyle = {
    fontSize: T.fsXxs, padding: "3px 8px", color: T.dark, whiteSpace: "nowrap",
  };
  const tdDescStyle = {
    ...tdStyle, color: T.mid, fontStyle: "italic", whiteSpace: "normal", maxWidth: 300,
  };

  // ── Mode 1: no existing rows → single incoming column (original behaviour) ──
  if (currentRows.length === 0) {
    return (
      <div
        data-testid="critical-dates-table"
        style={{ flex: 1, minWidth: 0, overflow: "auto" }}
      >
        <div style={{
          fontSize: T.fsXxxs, fontWeight: T.fwBold, color: T.blueDeep, marginBottom: 4,
          textTransform: "uppercase", letterSpacing: "0.5px",
          display: "flex", alignItems: "center", gap: 4,
        }}>
          <SourceBadge source={incomingSource} />
          <span>Incoming (this import)</span>
        </div>
        {incomingRows.length === 0 ? (
          <span style={{ fontSize: T.fsXxs, color: T.light, fontStyle: "italic" }}>
            No critical dates
          </span>
        ) : (
          <CornerTable style={{ borderCollapse: "collapse", width: "100%" }}>
            <thead>
              <tr>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Description</th>
              </tr>
            </thead>
            <tbody>
              {incomingRows.map((cd, i) => (
                <tr key={i} data-testid={`cd-row-${i}`}>
                  <td style={tdStyle}>
                    {criticalDateLabel(cd.type, cd.description)}
                  </td>
                  <td style={tdStyle}>
                    {fmtCdDate(cd.dueDate ?? cd.due_date)}
                  </td>
                  <td style={tdDescStyle}>
                    {cd.description || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </CornerTable>
        )}
      </div>
    );
  }

  // ── Mode 2: existing rows present → side-by-side Current | → | Incoming ─────
  // Build highlight maps: each key maps to "added" (incoming only) or
  // "removed" (current only). Rows present in both get no highlight.
  const currentKeySet  = new Set(currentRows.map(cdKey));
  const incomingKeySet = new Set(incomingRows.map(cdKey));

  // current side: "removed" if not in incoming
  const currentHighlight = new Map(
    currentRows.map((r) => [cdKey(r), incomingKeySet.has(cdKey(r)) ? null : "removed"])
  );
  // incoming side: "added" if not in current
  const incomingHighlight = new Map(
    incomingRows.map((r) => [cdKey(r), currentKeySet.has(cdKey(r)) ? null : "added"])
  );

  return (
    <div
      data-testid="critical-dates-table"
      style={{ display: "flex", gap: 12, flex: 1, minWidth: 0, overflow: "auto" }}
    >
      {/* Current (in WorkplaceOS) */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: T.fsXxxs, fontWeight: T.fwBold, color: T.mid, marginBottom: 2,
          textTransform: "uppercase", letterSpacing: "0.5px",
        }}>
          Current (in {APP_NAME})
        </div>
        <CdSingleTable
          rows={currentRows}
          highlightMap={currentHighlight}
          testPrefix="cd-current-row"
        />
      </div>

      {/* Arrow */}
      <div style={{
        display: "flex", alignItems: "center", color: T.light,
        fontSize: T.fsMd, flexShrink: 0, paddingTop: 16,
      }}>
        →
      </div>

      {/* Incoming (this import) */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: T.fsXxxs, fontWeight: T.fwBold, color: T.blueDeep, marginBottom: 2,
          textTransform: "uppercase", letterSpacing: "0.5px",
          display: "flex", alignItems: "center", gap: 4,
        }}>
          <SourceBadge source={incomingSource} />
          <span>Incoming (this import)</span>
        </div>
        <CdSingleTable
          rows={incomingRows}
          highlightMap={incomingHighlight}
          testPrefix="cd-incoming-row"
        />
      </div>
    </div>
  );
}

// ─── DecisionArea ─────────────────────────────────────────────────────────────

function DecisionArea({ diff, onDecide, disabled }) {
  const isApplied  = diff.appliedAt != null;
  const isAccepted = diff.status === "accepted";
  const isRejected = diff.status === "rejected";

  const handleAccept = () => onDecide(diff.id, isAccepted ? "pending" : "accepted");
  const handleReject = () => onDecide(diff.id, isRejected ? "pending" : "rejected");

  if (isApplied) {
    return (
      <div
        data-testid={`applied-pill-${diff.id}`}
        style={{ flexShrink: 0, display: "flex", justifyContent: "flex-end" }}
      >
        <span style={{
          fontSize: T.fsXs, fontWeight: T.fwBold, padding: "4px 12px", borderRadius: 5,
          background: T.utilGreen, color: T.utilGreenText, border: `1.5px solid ${T.successBorder}`,
          whiteSpace: "nowrap",
        }}>
          ✓ Applied
        </span>
      </div>
    );
  }

  // Semantic contract (matches apply handler):
  //   accepted = "I want the incoming value written" = Use incoming
  //   rejected = "I want the current value kept"     = Keep current
  // Unified verb pair for both new and changed fields.
  return (
    <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
      <DecideBtn
        label="Keep current"
        active={isRejected}
        activeStyle={{ bg: T.warnYellowBg, border: T.warnYellowBorder, color: T.amberDark }}
        onClick={handleReject}
        disabled={disabled}
        testId={`keep-current-btn-${diff.id}`}
      />
      <DecideBtn
        label="Use incoming"
        active={isAccepted}
        activeStyle={{ bg: T.utilGreen, border: T.successBorder, color: T.utilGreenText }}
        onClick={handleAccept}
        disabled={disabled || diff.canAccept === false}
        testId={`use-incoming-btn-${diff.id}`}
      />
    </div>
  );
}

// ─── DiffRow ──────────────────────────────────────────────────────────────────
//
// INGEST-REVIEW-ALIGN layout contract:
//
// • advisory variant  → display:contents outer + single gridColumn:"1/-1" inner
// • year_costs variant → display:contents outer + single gridColumn:"1/-1" inner
// • scalar variant    → display:contents outer + exactly 6 grid children:
//     [1] field cell  [2] current cell  [3] arrow cell  [4] incoming cell
//     [5] decision cell  [6] provenance row (gridColumn:"1/-1")
//
// The testid on the outer div (display:contents) is still queryable in the DOM;
// it produces no box of its own so it does not interfere with grid placement.

export function DiffRow({ diff, onDecide, disabled, readOnly }) {
  const isApplied  = diff.appliedAt != null;
  const isAccepted = diff.status === "accepted";
  const isRejected = diff.status === "rejected";
  const isNewField =
    diff.currentValue == null ||
    diff.currentValue === "" ||
    (Array.isArray(diff.currentValue) && diff.currentValue.length === 0);

  const rowBg = isApplied || isAccepted ? T.acceptedRowBg : isRejected ? T.redSoft : T.white;

  // ── advisory variant ───────────────────────────────────────────────────────
  if (diff.diffType === "advisory") {
    const warning =
      diff.incomingValue && typeof diff.incomingValue === "object"
        ? diff.incomingValue
        : null;
    return (
      <div data-testid={`diff-row-advisory-${diff.id}`} style={{ display: "contents" }}>
        <div style={{
          gridColumn: "1 / -1",
          display: "flex", alignItems: "flex-start", gap: 8,
          padding: "8px 10px",
          background: T.warnYellowBg,
          borderBottom: `1px solid ${T.warnYellowBorder}`,
        }}>
          <span style={{ fontSize: T.fsMd, lineHeight: "18px", flexShrink: 0 }}>⚠</span>
          <div>
            <div style={{ fontSize: T.fsXs, fontWeight: T.fwBold, color: T.amberDark, marginBottom: 2 }}>
              {warning?.code ?? "Data Warning"}
            </div>
            <div style={{ fontSize: T.fsXs, color: T.amberDark, lineHeight: "16px" }}>
              {warning?.message ?? String(diff.incomingValue ?? "")}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── year_costs variant ─────────────────────────────────────────────────────
  if (diff.fieldName === "year_costs") {
    return (
      <div data-testid={`diff-row-${diff.id}`} style={{ display: "contents" }}>
        <div style={{
          gridColumn: "1 / -1",
          padding: "8px 10px",
          background: rowBg,
          borderBottom: `1px solid ${T.border}`,
          transition: "background 0.12s",
        }}>
          {/* Header row */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
            <div style={{
              width: 140, flexShrink: 0,
              fontSize: T.fsSm, color: T.dark, fontWeight: T.fwSemibold,
            }}>
              Year costs
            </div>
            {diff.diffType === "conflict" && (
              <span
                data-testid="conflict-badge"
                style={{
                  fontSize: T.fsXxs, fontWeight: T.fwBold, padding: "2px 6px",
                  borderRadius: 4,
                  background: T.ragRedBg, color: T.ragRed,
                  flexShrink: 0, whiteSpace: "nowrap",
                }}
              >
                CONFLICT
              </span>
            )}
            {isAccepted && <RecChip action="apply" />}
            <div style={{ flex: 1 }} />
            {!readOnly && <DecisionArea diff={diff} onDecide={onDecide} disabled={disabled} />}
          </div>
          {diff.scheduleWarnings?.length > 0 && (
            <div role="alert" data-testid={`schedule-warning-${diff.id}`}
              style={{ color: T.warnAmberText, background: T.warnAmberBg, padding: 8, fontSize: T.fsSm }}>
              {diff.scheduleWarnings.map((warning) => <div key={warning}>{warning}</div>)}
              Review this row individually. Bulk acceptance excludes flagged schedules.
            </div>
          )}
          <YearCostTable
            currentValue={diff.currentValue}
            incomingValue={diff.incomingValue}
            incomingSource={diff.incomingSource}
          />
        </div>
      </div>
    );
  }

  // ── critical_dates variant ─────────────────────────────────────────────────
  if (diff.fieldName === "critical_dates") {
    const cdRows = parseCdArray(diff.incomingValue);
    const count  = cdRows.length;
    return (
      <div data-testid={`diff-row-${diff.id}`} style={{ display: "contents" }}>
        <div style={{
          gridColumn: "1 / -1",
          padding: "8px 10px",
          background: rowBg,
          borderBottom: `1px solid ${T.border}`,
          transition: "background 0.12s",
        }}>
          {/* Header row */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
            <div style={{
              width: 140, flexShrink: 0,
              fontSize: T.fsSm, color: T.dark, fontWeight: T.fwSemibold,
            }}>
              Critical dates
            </div>
            <span
              data-testid="cd-count-badge"
              style={{
                fontSize: T.fsXxs, fontWeight: T.fwBold, padding: "2px 7px",
                borderRadius: 8, background: T.blueSoft, color: T.blueDeep,
                flexShrink: 0, whiteSpace: "nowrap",
              }}
            >
              {count} critical date{count === 1 ? "" : "s"}
            </span>
            {isAccepted && <RecChip action="apply" />}
            <div style={{ flex: 1 }} />
            {!readOnly && <DecisionArea diff={diff} onDecide={onDecide} disabled={disabled} />}
          </div>
          <CriticalDatesTable
            currentValue={diff.currentValue}
            incomingValue={diff.incomingValue}
            incomingSource={diff.incomingSource}
          />
        </div>
      </div>
    );
  }

  // ── scalar variant: 5 grid cells + provenance span-all ────────────────────
  const isConflict = diff.diffType === "conflict";
  const isChanged  = !isNewField && (
    diff.currentValue !== diff.incomingValue ||
    diff.diffType === "conflict" ||
    diff.diffType === "provenance_promotion"
  );
  const delta       = isChanged ? computeDelta(diff.currentValue, diff.incomingValue) : null;
  const materialPct = isChanged ? computeMaterialChange(diff.fieldName, diff.currentValue, diff.incomingValue) : null;

  const cellBorder = `1px solid ${T.border}`;

  return (
    <div data-testid={`diff-row-${diff.id}`} style={{ display: "contents" }}>

      {/* ── Cell 1: Field label + chips ── */}
      <div style={{
        background: rowBg,
        borderBottom: cellBorder,
        padding: "8px 10px",
        display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 4, minWidth: 0,
        transition: "background 0.12s",
      }}>
        <span
          title={fieldLabel(diff.fieldName)}
          style={{
            fontSize: T.fsSm, color: T.dark, fontWeight: T.fwSemibold,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}
        >
          {fieldLabel(diff.fieldName)}
        </span>
        {isConflict && (
          <span
            data-testid="conflict-badge"
            style={{
              fontSize: T.fsXxs, fontWeight: T.fwBold, padding: "2px 6px",
              borderRadius: 4, background: T.ragRedBg, color: T.ragRed,
              flexShrink: 0, whiteSpace: "nowrap",
            }}
          >
            CONFLICT
          </span>
        )}
        {isNewField && !isAccepted && !isRejected && (
          <span
            data-testid={`new-chip-${diff.id}`}
            style={{
              fontSize: T.fsXxs, fontWeight: T.fwBold, padding: "2px 7px",
              borderRadius: 8, background: T.utilGreen, color: T.utilGreenText,
              flexShrink: 0, whiteSpace: "nowrap",
            }}
          >
            New
          </span>
        )}
        {isAccepted && <RecChip action="apply" />}
      </div>

      {/* ── Cell 2: Current value ── */}
      <div style={{
        background: T.bg,
        borderBottom: cellBorder,
        borderLeft: cellBorder,
        padding: "6px 8px",
        transition: "background 0.12s",
      }}>
        <div style={{
          fontSize: T.fsXxs, fontWeight: T.fwBold, color: T.mid,
          textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4,
        }}>
          Current (in {APP_NAME})
        </div>
        {isNewField ? (
          <span style={{ fontSize: T.fsSm, color: T.light, fontStyle: "italic" }}>
 - no current value - 
          </span>
        ) : (
          <span
            data-testid={`current-value-${diff.id}`}
            title={String(diff.currentValue ?? "")}
            style={{
              fontSize: T.fsSm,
              color: isChanged ? T.ragRed : T.dark,
              textDecoration: isChanged ? "line-through" : "none",
              fontWeight: T.fwSemibold,
              display: "block", overflow: "hidden", textOverflow: "ellipsis",
            }}
          >
            {fmtValue(diff.currentValue)}
          </span>
        )}
      </div>

      {/* ── Cell 3: Arrow ── */}
      <div style={{
        background: T.bg,
        borderBottom: cellBorder,
        display: "flex", alignItems: "center", justifyContent: "center",
        color: T.light, fontSize: T.fsSm,
      }}>
        →
      </div>

      {/* ── Cell 4: Incoming value ── */}
      <div style={{
        background: T.acceptedRowBg,
        borderBottom: cellBorder,
        borderRight: `1px solid ${T.greenBadge}`,
        padding: "6px 8px",
        transition: "background 0.12s",
      }}>
        <div style={{
          fontSize: T.fsXxs, fontWeight: T.fwBold, color: T.utilGreenText,
          textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4,
          display: "flex", alignItems: "center", gap: 4,
        }}>
          <SourceBadge source={diff.incomingSource} />
          <span>Incoming (this import)</span>
          {diff.confidence && <ConfidenceBadge confidence={diff.confidence} />}
        </div>
        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
          <span
            title={String(diff.incomingValue ?? "")}
            style={{
              fontSize: T.fsSm, fontWeight: T.fwBold, color: T.dark,
              display: "block", overflow: "hidden", textOverflow: "ellipsis",
            }}
          >
            {fmtValue(diff.incomingValue)}
          </span>
          {delta != null && <DeltaBadge delta={delta} />}
          {materialPct != null && <LargeChangeBadge pct={materialPct} />}
        </div>
        {diff.citation && (
          <div
            data-testid={`citation-${diff.id}`}
            style={{
              fontSize: T.fsXxs, color: T.mid, marginTop: 4,
              fontStyle: "italic",
            }}
          >
            📄 {diff.citation}
          </div>
        )}
        {diff.precedenceNote && (
          <div
            data-testid={`precedence-note-${diff.id}`}
            style={{
              fontSize: T.fsXxs, color: T.spacebaseBlue, marginTop: 3,
              fontStyle: "italic",
            }}
          >
            ℹ️ {diff.precedenceNote}
          </div>
        )}
      </div>

      {/* ── Cell 5: Decision buttons (hidden for readOnly/unmatched groups) ── */}
      <div style={{
        background: rowBg,
        borderBottom: cellBorder,
        padding: "8px 10px",
        display: "flex", alignItems: "center", justifyContent: "flex-end",
        transition: "background 0.12s",
      }}>
        {!readOnly && <DecisionArea diff={diff} onDecide={onDecide} disabled={disabled} />}
      </div>

      {/* ── Provenance: spans all 5 grid tracks ── */}
      <div style={{ gridColumn: "1 / -1" }}>
        <ProvenanceTimeline
          diffId={diff.id}
          currentProvenance={diff.currentProvenance}
          incomingSource={diff.incomingSource}
        />
      </div>

    </div>
  );
}
